import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  InsuranceCarrier,
  LiabilityInsurance,
  ProviderBlockOutDate,
  ProviderCredential,
  ProviderDisclosure,
  ProviderEmail,
  ProviderLicense,
  ProviderPhone,
  ProviderSpecialty,
  Provider,
  ProviderNameResult,
  ProviderOnboardingData,
  ProviderAuthRelease,
  ManagedProvider,
  ProviderStatusDisplayIdEnum,
} from "./schema"
import { PracticeName } from "../Practice/loaders/loader"
import { PracticeRoleSet } from "../Account/userSlice"
import { AppThunk } from "../../reduxStore/store"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import { ProviderTypeDisplayId } from "../enums/displayId.enum"

export interface ProviderState {
  provider: Provider
  providerName: ProviderNameResult
  providerPracticeNames: PracticeName[]
  providerPracticeRoles: PracticeRoleSet[]
  providerOnboardingData: ProviderOnboardingData
}

// Provider Practice Names
export const initialProviderPracticeName: PracticeName = {
  id: "",
  name: "",
}
const initialProviderPracticeNames: PracticeName[] = []

// Provider
export const initialInsuranceCarrier: InsuranceCarrier = {
  displayId: "",
  isHighlighted: false,
}

export const initialLiabilityInsurance: LiabilityInsurance = {
  id: "",
  fileName: "",
  fileId: "",
  issueDate: undefined,
  expirationDate: undefined,
  liabilityInsuranceStatusDisplayId: "",
  fileStatusDisplayId: "" as FileStatusDisplayIdEnum,
}

export const initialProviderCredential: ProviderCredential = {
  id: "",
  stateCode: "",
  expireDate: undefined,
  credentialTypeDisplayId: "",
}

export const initialProviderDisclosure: ProviderDisclosure = {
  id: "",
  fileName: "",
  fileId: "",
  isSupportingDocument: false,
  fileStatusDisplayId: "",
}

export const initialProviderEmail: ProviderEmail = {
  id: "",
  address: "",
  emailTypeDisplayId: undefined,
}

export const initialProviderLicense: ProviderLicense = {
  id: "",
  licenseNumber: "",
  stateCode: "",
  expireDate: undefined,
  fileName: "",
  fileId: "",
  licenseTypeDisplayId: "",
  licenseStatusDisplayId: "",
  fileStatusDisplayId: "",
  otherLicense: "",
}

export const initialProviderPhone: ProviderPhone = {
  id: "",
  number: "",
  countryCode: undefined,
  phoneTypeDisplayId: undefined,
}

export const initialProviderOnboardingData: ProviderOnboardingData = {
  affiliatedPractice: undefined,
  provider: null,
  userOnboarded: false,
}

const initialInsuranceCarriers: string[] = []
const initialLiabilityInsurances: LiabilityInsurance[] = []
const initialProviderBlockOutDates: ProviderBlockOutDate[] = []
const initialProviderCredentials: ProviderCredential[] = []
const initialProviderDisclosures: ProviderDisclosure[] = []
const initialProviderEmails: ProviderEmail[] = []
const initialProviderLicenses: ProviderLicense[] = []
const initialProviderPhones: ProviderPhone[] = []
const initialProviderSpecialties: ProviderSpecialty[] = []
const initialProviderAuthReleases: ProviderAuthRelease[] = []

export const providerToCreate: Partial<Provider> = {
  userId: "",
  providerStatusDisplayId: "pending",
  providerAgesSeenDisplayIds: [],
  providerAvailableDisplayIds: [],
  providerClientEthnicityDisplayIds: [],
  providerCommunityDisplayIds: [],
  providerDesignationDisplayIds: [],
  providerEthnicGroupDisplayIds: [],
  providerLanguageDisplayIds: [],
  providerParticipantDisplayIds: [],
  providerReligionDisplayIds: [],
  providerSessionTypeDisplayIds: [],
  providerSexualOrientationDisplayIds: [],
  providerTagDisplayIds: [],
  providerTherapyTypeDisplayIds: [],
  providerInsuranceDisplayIds: [],
  providerLocations: [],
  liabilityInsurances: [],
  providerBlockOutDate: [],
  providerCredentials: [],
  providerDisclosures: [],
  providerEmails: [],
  providerLicenses: [],
  providerPhones: [],
  providerSpecialties: [],
  providerAuthReleases: [],
  providerTelehealthPlatforms: [],
  isPublicViewable: true,
  isAcceptingNewPatients: true,
  hasPatientWaitList: false,
  isAvailableEveningHours: false,
  isLanguageAttested: false,
  isUnderSupervision: false,
  isInsurancePaneled: true,
  hasLicense: false,
  providerTypeDisplayId: ProviderTypeDisplayId.STANDARD,
}

export const managedProviderToCreate: ManagedProvider = {
  isPublicViewable: false,
  hasLicense: false,
  isAcceptingNewPatients: false,
  hasPatientWaitList: false,
  isAvailableEveningHours: false,
  isLanguageAttested: false,
  isUnderSupervision: false,
  isInsurancePaneled: false,
  providerStatusDisplayId: "pending",
  providerTypeDisplayId: ProviderTypeDisplayId.MANAGED,
  providerAgesSeenDisplayIds: [],
  providerAvailableDisplayIds: [],
  providerClientEthnicityDisplayIds: [],
  providerCommunityDisplayIds: [],
  providerDesignationDisplayIds: [],
  providerEthnicGroupDisplayIds: [],
  providerInsuranceDisplayIds: [],
  providerLanguageDisplayIds: [],
  providerParticipantDisplayIds: [],
  providerReligionDisplayIds: [],
  providerSessionTypeDisplayIds: [],
  providerSexualOrientationDisplayIds: [],
  providerTagDisplayIds: [],
  providerTelehealthPlatforms: [],
  providerTherapyTypeDisplayIds: [],
  providerLocations: [],
  liabilityInsurances: [],
  providerBlockOutDate: [],
  providerCredentials: [],
  providerDisclosures: [
    {
      isSupportingDocument: false,
    },
  ],
  providerEmails: [],
  providerLicenses: [],
  providerPhones: [],
  providerSpecialties: [],
  providerAuthReleases: [],
}

export const initialProvider: Provider = {
  id: "",
  userId: "",
  legacyAffiliateId: undefined,
  isPublicViewable: true,
  titleDisplayId: undefined,
  firstName: "",
  lastName: "",
  photoFileName: "",
  biography: "",
  practicingSince: "",
  gender: "",
  isTransgender: false,
  hasLicense: false,
  isAcceptingNewPatients: true,
  hasPatientWaitList: false,
  isAvailableEveningHours: false,
  isLanguageAttested: false,
  isUnderSupervision: false,
  graduateSchoolName: "",
  graduateYear: "",
  isInsurancePaneled: false,
  photoFileId: "",
  activationRequested: "",
  // Enum values (displayId)
  providerStatusDisplayId: "pending",
  photoFileStatusDisplayId: "",
  militaryStatusDisplayId: "",
  faithOrientationDisplayId: "",
  graduateDegreeDisplayId: "",
  providerTypeDisplayId: ProviderTypeDisplayId.STANDARD,
  // List of enum (multiple selections, but still enum values)
  providerAgesSeenDisplayIds: [],
  providerAvailableDisplayIds: [],
  providerClientEthnicityDisplayIds: [],
  providerCommunityDisplayIds: [],
  providerDesignationDisplayIds: [],
  providerEthnicGroupDisplayIds: [],
  providerLanguageDisplayIds: [],
  providerParticipantDisplayIds: [],
  providerReligionDisplayIds: [],
  providerSessionTypeDisplayIds: [],
  providerSexualOrientationDisplayIds: [],
  providerTagDisplayIds: [],
  providerTelehealthPlatforms: [],
  providerTherapyTypeDisplayIds: [],
  // External Foreign Keys
  providerLocations: [],
  // One to Many
  providerInsuranceDisplayIds: initialInsuranceCarriers,
  liabilityInsurances: initialLiabilityInsurances,
  providerBlockOutDate: initialProviderBlockOutDates,
  providerCredentials: initialProviderCredentials,
  providerDisclosures: initialProviderDisclosures,
  providerEmails: initialProviderEmails,
  providerLicenses: initialProviderLicenses,
  providerPhones: initialProviderPhones,
  providerSpecialties: initialProviderSpecialties,
  providerAuthReleases: initialProviderAuthReleases,
  createdByUserId: "",
  createdDateTime: "",
  npiNumber: "",
}
// Provider Names
export const initialProviderName: ProviderNameResult = {
  id: "",
  userId: "",
  firstName: "",
  lastName: "",
}

// Provider Practice Roles
export const initialProviderPracticeRoles: PracticeRoleSet[] = []

const initialState: ProviderState = {
  provider: initialProvider,
  providerName: initialProviderName,
  providerPracticeNames: initialProviderPracticeNames,
  providerPracticeRoles: initialProviderPracticeRoles,
  providerOnboardingData: initialProviderOnboardingData,
}

export const providerSlice = createSlice({
  name: "provider",
  initialState,
  reducers: {
    providerUpdated: (
      state: { provider: Provider },
      { payload }: PayloadAction<Provider>,
    ) => {
      state.provider = {
        ...state.provider,
        ...payload,
        // spread nested objects here as needed
      }
    },
    providerNameUpdated: (
      state: { providerName: ProviderNameResult },
      { payload }: PayloadAction<ProviderNameResult>,
    ) => {
      state.providerName = payload
    },
    providerPracticeNamesUpdated: (
      state: { providerPracticeNames: PracticeName[] },
      { payload }: PayloadAction<PracticeName[]>,
    ) => {
      state.providerPracticeNames = payload
    },
    providerPracticeRolesUpdated: (
      state: { providerPracticeRoles: PracticeRoleSet[] },
      { payload }: PayloadAction<PracticeRoleSet[]>,
    ) => {
      state.providerPracticeRoles = payload
    },
    providerOnboardingDataUpdated: (
      state: { providerOnboardingData: ProviderOnboardingData },
      { payload }: PayloadAction<ProviderOnboardingData>,
    ) => {
      state.providerOnboardingData = payload
    },
    resetProvider: (state: { provider: Provider }) => {
      state.provider = initialProvider
    },
    resetProviderPracticeNames: (state: {
      providerPracticeNames: PracticeName[]
    }) => {
      state.providerPracticeNames = initialProviderPracticeNames
    },
  },
})

// Export actions and reducer
export const {
  providerUpdated,
  providerNameUpdated,
  providerPracticeNamesUpdated,
  resetProvider,
  resetProviderPracticeNames,
} = providerSlice.actions

export const selectProvider = (state: {
  provider: ProviderState
}): Provider => {
  return state.provider.provider
}

export const selectProviderName = (state: {
  provider: ProviderState
}): ProviderNameResult => {
  return state.provider.providerName
}

export const selectProviderPracticeNames = (state: {
  provider: ProviderState
}): PracticeName[] => {
  return state.provider.providerPracticeNames
}

export const selectProviderPracticeRoles = (state: {
  provider: ProviderState
}): PracticeRoleSet[] => {
  return state.provider.providerPracticeRoles
}

export const selectProviderOnboardingData = (state: {
  provider: ProviderState
}): ProviderOnboardingData => {
  return state.provider.providerOnboardingData
}

export const updateUnquarantinedProviderLicenses =
  ({ licenses }: { licenses: ProviderLicense[] }): AppThunk =>
  async (dispatch, getState) => {
    const { provider } = getState().provider
    const updatedLicenses = licenses.filter(
      (license) =>
        license.fileStatusDisplayId !== FileStatusDisplayIdEnum.QUARANTINED,
    )
    const updatedProvider = {
      ...provider,
      providerLicenses: updatedLicenses,
    }
    dispatch(providerSlice.actions.providerUpdated(updatedProvider))
  }

export default providerSlice.reducer
